import { useApolloClient, useLazyQuery } from "@apollo/client";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { FormSelect } from "components/Forms/FormSelect";
import { FormTextField } from "components/Forms/FormTextField";
import {
  CharacterizationBasis,
  FlashPointQualifier,
  Issue,
  IssueLevel,
  PackingGroup,
  PhysicalState,
} from "generated-graphql/graphql";
import { useValidatingForm } from "hooks/useValidatingForm";
import { useCallback, useMemo, useState } from "react";
import { Control, Controller, useFieldArray } from "react-hook-form";

import {
  PROFILE_REFETCH_QUERIES,
  useSetWasteStreamDotProfile,
  useUpsertWasteProfile,
  WASTE_PROFILE_INPUT_VALIDATOR,
} from "./api";
import { BlockingIssues } from "./BlockingIssues";
import {
  prettyPrintCharacterizationBasis,
  prettyPrintFlashPointQualifier,
  prettyPrintPackingGroup,
  prettyPrintPhysicalState,
} from "./profileDisplay";
import { WasteStreamProfileData } from "./types";
import {
  hazardClassIssues,
  toDotInput,
  toFormValues,
  toProfileInput,
  WasteProfileFormValues,
} from "./wasteProfileForm";

const HALF = { xs: 12, sm: 6 };
const THIRD = { xs: 12, sm: 4 };

// The formatters return undefined for an unset value so read views can render a
// blank; here every enum member is mapped, so the fallback is unreachable and
// exists only to keep the option label a string.
const physicalStateItems = Object.values(PhysicalState).map((value) => ({
  value,
  display: prettyPrintPhysicalState(value) ?? value,
}));

const basisItems = Object.values(CharacterizationBasis).map((value) => ({
  value,
  display: prettyPrintCharacterizationBasis(value) ?? value,
}));

const flashPointQualifierItems = Object.values(FlashPointQualifier).map((value) => ({
  value,
  display: prettyPrintFlashPointQualifier(value) ?? value,
}));

const packingGroupItems = Object.values(PackingGroup).map((value) => ({
  value,
  display: prettyPrintPackingGroup(value) ?? value,
}));

/**
 * The four ways a flash point can be recorded, kept together.
 *
 * A vendor sheet usually states a range rather than a measurement, and two of its
 * options ("Does not flash", "Flammable solid") are not temperatures at all — so
 * these are peers, not a primary field with fallbacks. Extracted from the dialog
 * body because it is already long enough to trip the complexity check, and this
 * group reads as one question.
 */
function FlashPointFields({ control }: { control: Control<WasteProfileFormValues> }) {
  return (
    <>
      <Grid size={THIRD}>
        <FormTextField name="flashPointF" label="Flash point (°F)" control={control} fullWidth />
      </Grid>
      <Grid size={THIRD}>
        <FormTextField
          name="flashPointMinF"
          label="Flash point low (°F)"
          control={control}
          fullWidth
        />
      </Grid>
      <Grid size={THIRD}>
        <FormTextField
          name="flashPointMaxF"
          label="Flash point high (°F)"
          control={control}
          fullWidth
        />
      </Grid>
      <Grid size={THIRD}>
        <FormSelect
          name="flashPointQualifier"
          label="Flash point (non-numeric)"
          control={control}
          hideErrors
          selectItems={flashPointQualifierItems}
        />
      </Grid>
    </>
  );
}

/**
 * What the inline pass reports when it could not reach the engine at all. It
 * resolves with this rather than rejecting because react-hook-form's resolver
 * awaits the validator with no catch of its own, so a rejection would escape the
 * app instead of reaching anyone. Save runs the validator again and says what
 * went wrong; this only has to keep an unchecked profile from reading as a clean
 * one.
 */
const COULD_NOT_VALIDATE: Issue = {
  key: "validation",
  modelName: "WasteProfile",
  level: IssueLevel.Critical,
  message: "Could not validate the profile. Try again before saving.",
};

export const EditWasteProfileDialog = ({
  open,
  onClose,
  wasteStream,
  isReadOnly = false,
}: {
  open: boolean;
  onClose: () => void;
  wasteStream: WasteStreamProfileData;
  isReadOnly?: boolean;
}) => {
  const alerts = useAlerts();
  const client = useApolloClient();
  // The parent mounts this dialog only while it is open, so defaultValues are
  // read fresh on every open and no reset effect is needed to pick up a save.
  const [upsertProfile, { loading: savingProfile }] = useUpsertWasteProfile();
  const [setDotProfile, { loading: savingDot }] = useSetWasteStreamDotProfile();
  const [runValidator, { loading: validating }] = useLazyQuery(WASTE_PROFILE_INPUT_VALIDATOR, {
    fetchPolicy: "network-only",
  });

  /**
   * `wasteProfileSchema` in the compliance engine, reached through its validator
   * query. Its issue keys are already form paths — the engine numbers a list
   * element `constituents.0.name`, which is how react-hook-form addresses a field
   * array — so an issue lands on the offending input with nothing in between.
   */
  const validate = useCallback(
    async (values: WasteProfileFormValues): Promise<Issue[]> => {
      try {
        const result = await runValidator({
          variables: { input: toProfileInput(wasteStream.id, values) },
        });
        if (result.error) throw result.error;
        return [...(result.data?.wasteProfileInputValidator ?? []), ...hazardClassIssues(values)];
      } catch {
        return [COULD_NOT_VALIDATE];
      }
    },
    [runValidator, wasteStream.id]
  );

  // Memoized because `useValidatingForm` resets the form whenever this value's
  // identity changes; rebuilding it inline resets on every render and spins. The
  // parent mounts this dialog only while it is open, so it is still read fresh on
  // every open.
  const defaultValues = useMemo(() => toFormValues(wasteStream), [wasteStream]);
  const { control, handleSubmit, getValues } = useValidatingForm<WasteProfileFormValues>(
    defaultValues,
    [],
    validate,
    [],
    // The submit path always refetches the profile, which changes `wasteStream`
    // and so resets this form. On the partial-save path — profile written, DOT
    // write failed, dialog still open — that reset would revert the DOT values the
    // user typed, on the one path where they were not saved and have to be
    // retried. Keeping dirty values is what the old `useForm` did by never
    // resetting at all.
    { keepDirtyValuesOnReset: true }
  );
  const { fields, append, remove } = useFieldArray({ control, name: "constituents" });
  const [issues, setIssues] = useState<string[]>([]);
  // Keys the reasons list, so each refused Save remounts it and it takes focus again.
  const [refusals, setRefusals] = useState(0);

  // The final refetch is not tracked by any mutation hook, and a partial save
  // leaves the dialog open, so without this Save re-enables while the profile
  // is still reloading and a second click can fire against stale form state.
  const [refreshing, setRefreshing] = useState(false);
  const saving = savingProfile || savingDot || validating || refreshing;

  const onSubmit = async (values: WasteProfileFormValues) => {
    const input = toProfileInput(wasteStream.id, values);

    // `wasteProfileSchema` in the compliance engine — the same rules the
    // AI-extraction apply path runs, so the two surfaces cannot drift on what a
    // self-consistent profile is.
    //
    // Blocking the save on them is this dialog's decision, not the mutation's.
    // They are ERROR-level: a characterization that disagrees with itself is a
    // problem to resolve, not a write the database cannot take, so
    // `upsertWasteProfile` would accept it. Stopping here is what keeps someone
    // from saving a contradiction they have not looked at.
    //
    // A failure here has to be reported, not swallowed: the lazy query
    // rejects on a network or authorization error, and an unhandled rejection
    // would abandon the submit with the dialog still open and nothing said —
    // the same "Save did nothing" symptom this PR already fixed once. Its
    // resolved-but-errored form is just as bad, because an undefined result
    // reads as zero issues and would wave an unvalidated profile through.
    let validation;
    try {
      const result = await runValidator({ variables: { input } });
      if (result.error) throw result.error;
      validation = result.data;
    } catch (error) {
      alerts.error(
        "Could not validate the profile",
        error instanceof Error ? error.message : undefined
      );
      return;
    }
    const messages = [
      ...(validation?.wasteProfileInputValidator ?? []),
      ...hazardClassIssues(values),
    ].map((issue) => issue.message);
    if (messages.length > 0) {
      setIssues(messages);
      setRefusals((count) => count + 1);
      return;
    }
    setIssues([]);

    let profileCommitted = false;
    try {
      // Two mutations because the DOT block lives on WasteStream while the
      // characterization lives on WasteProfile. The profile goes first: it is
      // the row that may not exist yet, and a failure there should not leave
      // a DOT change stranded against a stream with no profile.
      //
      // Both suppress their hook's own refetch (`refetchQueries: []`) because
      // they refetch the SAME queries. Left to their defaults, the profile
      // write's refetch is issued before the DOT write lands, so whichever
      // response arrives last wins — and when it's that first one, the DOT
      // block reads back as empty even though it saved. One refetch after
      // both writes is the only ordering that can't race.
      await upsertProfile({ variables: { input }, refetchQueries: [] });
      profileCommitted = true;
      await setDotProfile({
        variables: { input: toDotInput(wasteStream.id, values) },
        refetchQueries: [],
      });
      alerts.success("Profile saved");
      onClose();
    } catch (error) {
      const detail = error instanceof Error ? error.message : undefined;
      // The two writes aren't one transaction, so a DOT failure after the
      // profile committed is a partial save. Saying "failed to save profile"
      // there would send the user back to re-enter characterization fields
      // that are already stored.
      if (profileCommitted) {
        alerts.error("Characterization saved, but the DOT fields did not", detail);
      } else {
        alerts.error("Failed to save profile", detail);
      }
    } finally {
      // In `finally` so a failed DOT write still refreshes the profile write
      // that already committed, rather than leaving the drawer stale.
      setRefreshing(true);
      try {
        await client.refetchQueries({ include: PROFILE_REFETCH_QUERIES });
      } finally {
        setRefreshing(false);
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      fullWidth
      title="Edit characterization"
      primaryActionButtonProps={{
        loading: saving,
        onClick: handleSubmit(onSubmit),
        disabled: isReadOnly,
      }}
    >
      <Stack spacing={3} sx={{ pt: 1 }}>
        {issues.length > 0 && (
          <BlockingIssues key={refusals} title="Fix these before saving" messages={issues} />
        )}

        <Stack spacing={2}>
          <Typography variant="subtitle2">Physical properties</Typography>
          <Grid container spacing={2}>
            <Grid size={THIRD}>
              <FormSelect
                name="physicalState"
                label="Physical state"
                control={control}
                hideErrors
                selectItems={physicalStateItems}
              />
            </Grid>
            <Grid size={THIRD}>
              <FormTextField name="phLow" label="pH low" control={control} fullWidth />
            </Grid>
            <Grid size={THIRD}>
              <FormTextField name="phHigh" label="pH high" control={control} fullWidth />
            </Grid>
            <Grid size={THIRD}>
              <FormTextField
                name="specificGravity"
                label="Specific gravity"
                control={control}
                fullWidth
              />
            </Grid>
            <FlashPointFields control={control} />
            <Grid size={HALF}>
              <FormTextField
                name="federalWasteCodes"
                label="EPA waste codes"
                helperText="Comma separated, e.g. D001, F003"
                control={control}
                fullWidth
              />
            </Grid>
            <Grid size={HALF}>
              <FormTextField
                name="stateWasteCodes"
                label="State waste codes"
                helperText="Comma separated"
                control={control}
                fullWidth
              />
            </Grid>
            <Grid size={THIRD}>
              <FormSelect
                name="basis"
                label="Basis"
                control={control}
                hideErrors
                selectItems={basisItems}
              />
            </Grid>
            <Grid size={12}>
              <FormTextField
                name="physicalDescription"
                label="Physical description"
                control={control}
                fullWidth
                textFieldProps={{ multiline: true, minRows: 2 }}
              />
            </Grid>
            <Grid size={12}>
              <FormTextField
                name="generatingProcess"
                label="Generating process"
                control={control}
                fullWidth
                textFieldProps={{ multiline: true, minRows: 2 }}
              />
            </Grid>
          </Grid>
        </Stack>

        <Divider />

        <Stack spacing={1}>
          <Typography variant="subtitle2">Hazard characteristics</Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            {(
              [
                ["isIgnitable", "Ignitable"],
                ["isCorrosive", "Corrosive"],
                ["isReactive", "Reactive"],
                ["isToxic", "Toxic"],
              ] as const
            ).map(([name, label]) => (
              <Controller
                key={name}
                name={name}
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!field.value}
                        onChange={(event) => field.onChange(event.target.checked)}
                      />
                    }
                    label={label}
                  />
                )}
              />
            ))}
          </Stack>
        </Stack>

        <Divider />

        <Stack spacing={2}>
          <Typography variant="subtitle2">Profile identity</Typography>
          <Grid container spacing={2}>
            <Grid size={THIRD}>
              <FormTextField
                name="profileNumber"
                label="Profile number"
                control={control}
                fullWidth
              />
            </Grid>
            <Grid size={THIRD}>
              <FormTextField name="tsdfName" label="TSDF" control={control} fullWidth />
            </Grid>
            <Grid size={THIRD}>
              <FormTextField
                name="tsdfProfileId"
                label="TSDF profile ID"
                control={control}
                fullWidth
              />
            </Grid>
          </Grid>
        </Stack>

        <Divider />

        <Stack spacing={2}>
          <Typography variant="subtitle2">DOT</Typography>
          <Grid container spacing={2}>
            <Grid size={HALF}>
              <FormTextField
                name="dotProperShippingName"
                label="Proper shipping name"
                control={control}
                fullWidth
              />
            </Grid>
            <Grid size={THIRD}>
              <FormTextField name="dotIdNumber" label="UN/NA ID" control={control} fullWidth />
            </Grid>
            <Grid size={THIRD}>
              <FormTextField
                name="dotHazardClass"
                label="Hazard class"
                control={control}
                fullWidth
              />
            </Grid>
            <Grid size={THIRD}>
              <FormTextField
                name="dotSubsidiaryHazardClass"
                label="Subsidiary hazard class"
                control={control}
                fullWidth
                helperText="Prints as a second hazard label with its own class number."
              />
            </Grid>
            <Grid size={THIRD}>
              <FormSelect
                name="dotPackingGroup"
                label="Packing group"
                control={control}
                hideErrors
                selectItems={packingGroupItems}
              />
            </Grid>
            <Grid size={THIRD}>
              <Controller
                name="dotHazardous"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!field.value}
                        onChange={(event) => field.onChange(event.target.checked)}
                      />
                    }
                    label="DOT hazardous"
                  />
                )}
              />
            </Grid>
          </Grid>
        </Stack>

        <Divider />

        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2">Constituents</Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() =>
                append({ name: "", casNumber: "", minPercent: null, maxPercent: null })
              }
            >
              Add constituent
            </Button>
          </Stack>
          {fields.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No constituents yet.
            </Typography>
          )}
          {fields.map((field, index) => (
            <Grid container spacing={1} key={field.id} alignItems="center">
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormTextField
                  name={`constituents.${index}.name`}
                  label="Name"
                  control={control}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <FormTextField
                  name={`constituents.${index}.casNumber`}
                  label="CAS number"
                  control={control}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 2 }}>
                <FormTextField
                  name={`constituents.${index}.minPercent`}
                  label="Min %"
                  control={control}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 2 }}>
                <FormTextField
                  name={`constituents.${index}.maxPercent`}
                  label="Max %"
                  control={control}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 1 }}>
                <IconButton
                  aria-label={`Remove constituent ${getValues(`constituents.${index}.name`) || index + 1}`}
                  onClick={() => remove(index)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Grid>
            </Grid>
          ))}
        </Stack>
      </Stack>
    </Dialog>
  );
};
